import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomizationService } from '@app/core/services/customization.service';
import { ImageService } from '@app/core/services/image.service';
import { PromotionService } from '@app/core/services/promotion.service';
import * as fromRoot from '@app/reducers';
import { Promotion } from '@app/shared/models/promotion';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { defer, forkJoin, from, Observable, of, retry, timer } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as fromUploader from '../reducers';
import { PrepareActions } from '../actions';
import { Kyc, errors } from '@smash-sdk/kyc/ejs/11-2023';
import { Image } from '@smash-sdk/image/ejs/10-2019/image';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { Constant } from 'src/constant';
import GetEmailChallengeError from '@smash-sdk/kyc/ejs/11-2023/types/GetEmailChallenge/GetEmailChallengeError';
import CreateEmailChallengeError from '@smash-sdk/kyc/ejs/11-2023/types/CreateEmailChallenge/CreateEmailChallengeError';

/**
 * Retries a customization list HTTP call on TRANSIENT server errors only — HTTP >= 500 or status 0
 * (network unreachable) — up to 2 times with a short backing-off delay, then rethrows so the
 * caller's catchError can degrade gracefully. Client errors (4xx) rethrow immediately (no point
 * retrying an auth/not-found). This spares a configured (white-label) domain from losing its branding
 * on a brief blip, without turning a hard failure into a hang.
 */
function retryTransientHttp<T>(): (source: Observable<T>) => Observable<T> {
  return retry<T>({
    delay: (error, retryCount) => {
      const transient = error instanceof HttpErrorResponse && (error.status >= 500 || error.status === 0);
      if (!transient || retryCount > 2) {
        throw error;
      }
      return timer(300 * retryCount);
    },
  });
}

@Injectable()
export class PrepareEffects {

  addBackgroundImageToAttach = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.addBackgroundImageToAttach),
      map(action => action.image),
      concatMap(([image]: any[]) => {
        const imageSdk = new Image();
        return forkJoin([of(image), from(imageSdk.createImage())])
      }),
      concatMap(([image, res]: any[]) => forkJoin([of(res), this.imageService.uploadImage(res.image.uploadUrl, image)])),
      concatMap(([res, _]: any[]) => {
        const background = { id: res.image.id, sourceUrl: res.image.downloadUrl, thumbnail: res.image.downloadUrl, type: 'Image' };
        return of(PrepareActions.addBackgroundImageToAttachSuccess({ background }));
      })
    )
  );

  addBackgroundVideoToAttach = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.addBackgroundVideoToAttach),
      map(action => action.video),
      concatMap((video: any) => {
        const { type, sourceId } = video;
        if (type === 'Youtube') {
          const thumbnail = 'i1.ytimg.com/vi/' + sourceId + '/2.jpg';
          const videoToReturn = { ...video, thumbnail };
          return of(PrepareActions.addBackgroundVideoToAttachSuccess({ background: videoToReturn }));
        } else if (type === 'Vimeo') {
          return forkJoin([this.promotionService.getThumbnailVimeo(video.sourceId)]).pipe(map(([res]) => {
            const thumbnail = res[0].thumbnail_medium.replace('https://', '');
            const videoToReturn = { ...video, thumbnail };
            return PrepareActions.addBackgroundVideoToAttachSuccess({ background: videoToReturn });
          }));
        }
      })
    )
  );

  addLogoToAttach = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.addLogoToAttach),
      map(action => action.image),
      concatMap(([image]: any[]) => {
        const imageSdk = new Image();
        return forkJoin([of(image), from(imageSdk.createImage())])
      }),
      concatMap(([image, res]: any[]) => forkJoin([of(res), this.imageService.uploadImage(res.image.uploadUrl, image)])),
      concatMap(([res, _]: any[]) => {
        const logo = { id: res.image.id, sourceUrl: res.image.downloadUrl, thumbnail: res.image.downloadUrl, type: 'Image' };
        return of(PrepareActions.addLogoToAttachSuccess({ logo }));
      })
    )
  );

  loadCustomization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadCustomization),
      withLatestFrom(this.store$.pipe(select(fromRoot.getCurrentDomain))),
      exhaustMap(([_, domain]) => {
        return this.customizationService.listManagedCustomizations().pipe(retryTransientHttp(), mergeMap(({ customizations: managedCustomizations }) => {
          return this.customizationService.getManagedCustomization(managedCustomizations[0]?.id).pipe(mergeMap(({ customization: managedCustomization }) => {
            if (domain?.team) {
              return this.customizationService.listTeamCustomizations(domain.team).pipe(
                mergeMap(({ customizations: teamCustomizations }) => {
                  if (teamCustomizations?.length) {
                    return this.customizationService.getTeamCustomization(domain.team, teamCustomizations[0].id).pipe(
                      mergeMap(({ customization }) => {
                        if (customization?.logo && customization?.background) {
                          return [
                            PrepareActions.loadCustomizationSuccess({ customization })
                          ];
                        } else {
                          const logo = customization.logo ? customization.logo : managedCustomization.logo;
                          const background = customization.background ? customization.background : managedCustomization.background;
                          return [
                            PrepareActions.loadCustomizationSuccess({ customization: { ...customization, logo, background } })
                          ];
                        }
                      }));
                  } else {
                    return [
                      PrepareActions.loadCustomizationSuccess({ customization: managedCustomization })
                    ];
                  }
                }),
                catchError(error => {
                  return [
                    PrepareActions.loadCustomizationSuccess({ customization: managedCustomization })
                  ]
                }));
            } else {
              return [
                PrepareActions.loadCustomizationSuccess({ customization: managedCustomization })
              ];
            }
          }),
            catchError(error => {
              return [PrepareActions.loadCustomizationFailure({ error: error.message })]
            }));
        }),
          catchError(error => {
            // The ROOT listManagedCustomizations call had no error handling: a failure here (500,
            // timeout, ...) dispatched neither Success nor Failure, so customizationLoading stayed
            // true, customizationError stayed null, and ResolveCustomizationService#resolve() never
            // emitted -> the /prepare "Obtenir un lien" hang. Surface it as a customization failure,
            // exactly like the inner getManagedCustomization catchError above, so the resolver's
            // customizationError branch resolves and the upload proceeds without branding.
            return [PrepareActions.loadCustomizationFailure({ error: error.message })];
          }));
      })
    )
  );

  loadCustomizationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadCustomizationSuccess),
      // customization is null on the (now-supported) no-branding path — loadCustomization$ dispatches
      // loadCustomizationSuccess({ customization: managedCustomization }) where managedCustomization can
      // be null. Optional chaining avoids a TypeError; the SetTransferCustomization reducer already
      // tolerates undefined logo/background (logo?.sourceUrl / background?.sourceUrl).
      map(({ customization }) => PrepareActions.SetTransferCustomization({ logo: customization?.logo, background: customization?.background }))
    )
  );

  loadBackgrounds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadBackgrounds),
      withLatestFrom(this.store$.pipe(select(fromRoot.getCurrentDomain))),
      exhaustMap(([_, domain]) => {
        return this.customizationService.listManagedBackgrounds().pipe(retryTransientHttp(), mergeMap(({ backgrounds: managedBackgrounds }) => {
          if (domain?.team) {
            return this.customizationService.listTeamBackgrounds(domain.team).pipe(
              mergeMap((({ backgrounds }) => {
                if (backgrounds?.length) {
                  return of(PrepareActions.loadBackgroundsSuccess({ backgrounds }));
                } else {
                  return of(PrepareActions.loadBackgroundsSuccess({ backgrounds: managedBackgrounds }));
                }
              })),
              catchError(error => {
                return [PrepareActions.loadBackgroundsSuccess({ backgrounds: managedBackgrounds })]
              }));
          } else {
            return of(PrepareActions.loadBackgroundsSuccess({ backgrounds: managedBackgrounds }));
          }
        }),
          catchError(error => {
            // The ROOT listManagedBackgrounds call had no error handling: a failure here left
            // backgroundsLoading stuck true and the resolver hanging. Fall back to no backgrounds --
            // the same graceful degradation the team branch above already applies on its own failure
            // -- so backgroundsLoaded turns true and the resolver can emit.
            return of(PrepareActions.loadBackgroundsSuccess({ backgrounds: [] }));
          }));
      }))
  );

  loadLogos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadLogos),
      withLatestFrom(this.store$.pipe(select(fromRoot.getCurrentDomain))),
      exhaustMap(([_, domain]) => {
        return this.customizationService.listManagedLogos().pipe(retryTransientHttp(), mergeMap(({ logos: managedLogos }) => {
          if (domain?.team) {
            return this.customizationService.listTeamLogos(domain.team).pipe(
              mergeMap((({ logos }) => {
                if (logos?.length) {
                  return of(PrepareActions.loadLogosSuccess({ logos }));
                } else {
                  return of(PrepareActions.loadLogosSuccess({ logos: managedLogos }));
                }
              })),
              catchError(error => {
                return [PrepareActions.loadLogosSuccess({ logos: managedLogos })]
              }));
          } else {
            return of(PrepareActions.loadLogosSuccess({ logos: managedLogos }));
          }
        }),
          catchError(error => {
            // The ROOT listManagedLogos call had no error handling: a failure here left logosLoading
            // stuck true and the resolver hanging. Fall back to no logos -- the same graceful
            // degradation the team branch above already applies on its own failure -- so logosLoaded
            // turns true and the resolver can emit.
            return of(PrepareActions.loadLogosSuccess({ logos: [] }));
          }));
      }))
  );

  loadExternalPromotions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadExternalPromotions),
      withLatestFrom(
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.getSelectedLanguage),
      ),
      concatMap(([_, domain, language]) => {
        let req: Observable<{ promotions: Promotion[] }>;
        if (domain.team) {
          req = this.promotionService.listPromotions(domain.team);
        } else {
          req = this.promotionService.listManagedPromotions();
        }
        return req.pipe(
          mergeMap(({ promotions }) => {
            const filteredPromotions = promotions.filter(p => p.language === language);
            if (filteredPromotions.length && domain.team) {
              return [
                PrepareActions.selectRandomExternalPromotion({ promotions: filteredPromotions as [Promotion, ...Promotion[]] }),
              ];
            }
            return [PrepareActions.loadExternalPromotionsSuccess({ promotions: [] })];

          }),
          catchError(error => {
            return of(PrepareActions.loadExternalPromotionsFailure({ error: error.message }))
          })
        );
      })
    ));

  selectRandomExternalPromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.selectRandomExternalPromotion),
      withLatestFrom(this.store$.select(fromRoot.getCurrentDomain)),
      concatMap(([action, domain]) => {
        const selectedPromotion = action.promotions[Math.floor(Math.random() * action.promotions.length)];
        return this.promotionService.getTeamPromotion(domain.team, selectedPromotion.id).pipe(
          mergeMap(({ promotion }) => [
            PrepareActions.selectRandomExternalPromotionSuccess({ promotion }),
            PrepareActions.loadExternalPromotionsSuccess({ promotions: action.promotions }),
            PrepareActions.SetTransferPromotion({ promotion }),
          ]),
          // Must settle the PLURAL externalPromotions* flags the promotion resolver watches
          // (resolve-external-promotion.ts). Dispatching the SINGULAR loadPromotionFailure left
          // externalPromotionsLoading stuck true, so the resolver never emitted and upload$'s
          // forkJoin hung /prepare exactly like the customization deadlock. loadExternalPromotionsFailure
          // sets externalPromotionsError, which the resolver's `promotionLoaded || promotionError`
          // branch resolves — degrading to no promotion instead of blocking the upload.
          catchError(error => of(PrepareActions.loadExternalPromotionsFailure({ error: error.message })))
        );
      })
    ));

  loadPromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadPromotion),
      withLatestFrom(this.store$.select(fromRoot.getCurrentDomain)),
      concatMap(([action, domain]) => {
        if (!domain?.team) {
          return [{ type: 'noop' }]
        }
        return this.promotionService.getTeamPromotion(domain.team, action.promotion.id).pipe(
          map((resp) => {
            return PrepareActions.loadPromotionSuccess({ promotion: resp.promotion });
          }),
          catchError((error: HttpErrorResponse) =>
            of(PrepareActions.loadPromotionFailure({ error: error.message }))
          )
        );
      })
    ));

  loadPromotionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadPromotionSuccess),
      map(({ promotion }) => PrepareActions.SetTransferPromotion({ promotion }))
    )
  );

  verifyEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.VerifyEmail),
      withLatestFrom(this.store$.select(fromRoot.getSelectedLanguage)),
      concatMap(([{ username }, language]) => {
        const kycSdk = new Kyc();
        return defer(() => kycSdk.getEmailChallenge({ email: username })).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: GetEmailChallengeError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: GetEmailChallengeError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              { error: GetEmailChallengeError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          concatMap(({ emailChallenge }) => {
            if (emailChallenge.status === 'Dirty') {
              return [
                PrepareActions.SendEmailVerificationCode({ username }),
                PrepareActions.VerifyEmailSuccess({ status: 'Dirty' }),
              ];
            }
            return of(PrepareActions.VerifyEmailSuccess({ status: emailChallenge.status }));
          }),
          catchError((error) => {
            if (error instanceof errors.GetEmailChallengeError.NotFoundError) {
              return [
                PrepareActions.SendEmailVerificationCode({ username }),
                PrepareActions.VerifyEmailSuccess({ status: 'Dirty' }),
              ];
            }
            return of(PrepareActions.VerifyEmailFailure({ error: error.message }));
          })
        )
      })
    )
  );

  validateEmailVerificationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.ValidateEmailVerificationCode),
      concatMap(({ username, code }) => {
        const kycSdk = new Kyc();
        return defer(() => kycSdk.updateEmailChallenge({ email: username, token: code })).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: errors.UpdateEmailChallengeError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: errors.UpdateEmailChallengeError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              { error: errors.UpdateEmailChallengeError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          map(({ emailChallenge }) => {
            return PrepareActions.ValidateEmailVerificationCodeSuccess({ status: emailChallenge.status });
          }),
          catchError((error) => {
            if (error instanceof errors.UpdateEmailChallengeError.InvalidTokenError) {
              return of(PrepareActions.ValidateEmailVerificationCodeFailure({ error: error.message }));
            }
            return of(PrepareActions.ValidateEmailVerificationCodeFailure({ error: error.message }));
          })
        );
      }),
      catchError((error: HttpErrorResponse) =>
        of(PrepareActions.ValidateEmailVerificationCodeFailure({ error: error.message }))
      )
    )
  );

  sendEmailVerificationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.SendEmailVerificationCode),
      withLatestFrom(this.store$.select(fromRoot.getSelectedLanguage)),
      concatMap(([{ username }, language]) => {
        const kycSdk = new Kyc();
        return defer(() => kycSdk.createEmailChallenge({ email: username, language })).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: CreateEmailChallengeError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: CreateEmailChallengeError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              { error: CreateEmailChallengeError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          map(({ emailChallenge }) => {
            return PrepareActions.SendEmailVerificationCodeSuccess({ status: emailChallenge.status });
          }),
          catchError((error) => {
            if (error instanceof errors.CreateEmailChallengeError.NotificationAlreadySentError) {
              return of(PrepareActions.SendEmailVerificationCodeFailure({ error: error.message }));
            }
            return of(PrepareActions.SendEmailVerificationCodeFailure({ error: error.message }));
          })
        );
      }),
      catchError((error: HttpErrorResponse) =>
        of(PrepareActions.VerifyEmailFailure({ error: error.message }))
      )
    )
  );

  constructor(
    private actions$: Actions,
    private promotionService: PromotionService,
    private customizationService: CustomizationService,
    private store$: Store<fromUploader.State & fromRoot.State>,
    private imageService: ImageService,
  ) { }

}
